import { formatPriceDollar } from '@/biz/order';
import { TrashIcon } from '@/components/common/icon';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

function getFairRangeLayout(low: number, high: number) {
  const padding = low / 2;
  const scaleMin = low - padding;
  const scaleMax = high + padding;
  const totalRange = scaleMax - scaleMin;

  return {
    scaleMin,
    scaleMax,
    totalRange,
    leftOffsetPercent: ((low - scaleMin) / totalRange) * 100,
    gradientWidthPercent: ((high - low) / totalRange) * 100
  };
}

function valueToPercent(value: number, scaleMin: number, totalRange: number) {
  return ((value - scaleMin) / totalRange) * 100;
}

function getClampedPercent(
  value: number | undefined,
  scaleMin: number,
  totalRange: number
) {
  if (isEmptyFairRangeValue(value)) return null;
  return Math.max(
    0,
    Math.min(100, valueToPercent(Number(value), scaleMin, totalRange))
  );
}

function percentToValue(percent: number, scaleMin: number, totalRange: number) {
  return Math.round(scaleMin + (percent / 100) * totalRange);
}

function getFairRangeDefaultValue(low: number, high: number) {
  return Math.round((low + high) / 2);
}

function isEmptyFairRangeValue(value?: number | string | null) {
  if (value === undefined || value === null || value === '') return true;
  const numericValue = Number(value);
  return Number.isNaN(numericValue) || numericValue <= 0;
}

export { getFairRangeDefaultValue, isEmptyFairRangeValue };

const THUMB_WIDTH = 48;
const THUMB_HEIGHT = 24;
// viewBox 内拇指矩形中心，用于在 83×59 图标内对齐
const THUMB_ICON_CENTER_X = 33.4632;
const THUMB_ICON_CENTER_Y = 19.4632;
// 两个标记位置接近时视为重叠，tooltip 分上下展示
const MARKER_OVERLAP_THRESHOLD = 12;
// 靠近轨道两端时改为左/右对齐，避免 tooltip 溢出容器
const MARKER_EDGE_THRESHOLD = 15;

function getMarkerHorizontalAlign(percent: number): 'start' | 'center' | 'end' {
  if (percent <= MARKER_EDGE_THRESHOLD) return 'start';
  if (percent >= 100 - MARKER_EDGE_THRESHOLD) return 'end';
  return 'center';
}

function getMarkerTransform(align: 'start' | 'center' | 'end') {
  if (align === 'start') return 'translate(0, -50%)';
  if (align === 'end') return 'translate(-100%, -50%)';
  return 'translate(-50%, -50%)';
}

function getMarkerLeft(percent: number, align: 'start' | 'center' | 'end') {
  if (align === 'start') return '0%';
  if (align === 'end') return '100%';
  return `${percent}%`;
}

function TooltipCaret({ direction }: { direction: 'up' | 'down' }) {
  if (direction === 'down') {
    return (
      <svg
        width="10"
        height="5"
        viewBox="0 0 10 5"
        className="-mt-px block"
        aria-hidden
      >
        <path
          d="M0 0 H10 L5 5 Z"
          fill="#fff"
          stroke="#E7E5E4"
          strokeWidth="1"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg
      width="10"
      height="5"
      viewBox="0 0 10 5"
      className="-mb-px block"
      aria-hidden
    >
      <path
        d="M0 5 H10 L5 0 Z"
        fill="#fff"
        stroke="#E7E5E4"
        strokeWidth="1"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MarkerTooltip({
  children,
  placement,
  horizontalAlign = 'center'
}: {
  children: React.ReactNode;
  placement: 'above' | 'below';
  horizontalAlign?: 'start' | 'center' | 'end';
}) {
  const horizontalClass =
    horizontalAlign === 'start'
      ? 'left-0 items-start'
      : horizontalAlign === 'end'
        ? 'right-0 items-end'
        : 'left-1/2 -translate-x-1/2 items-center';

  return (
    <div
      className={`absolute flex flex-col drop-shadow-[0_1px_2px_rgba(0,0,0,0.05)] ${horizontalClass} ${
        placement === 'above'
          ? 'bottom-[calc(100%+4px)]'
          : 'top-[calc(100%+4px)]'
      }`}
    >
      {placement === 'below' ? <TooltipCaret direction="up" /> : null}
      <div className="whitespace-nowrap rounded-md border border-[#E7E5E4] bg-white px-2 py-1 text-xs font-medium leading-4 text-[#44403C]">
        {children}
      </div>
      {placement === 'above' ? <TooltipCaret direction="down" /> : null}
    </div>
  );
}

function OfferMarker({
  label,
  amount,
  percent,
  placement = 'above'
}: {
  label: string;
  amount: number;
  percent: number;
  placement?: 'above' | 'below';
}) {
  const horizontalAlign = getMarkerHorizontalAlign(percent);

  return (
    <div
      className="absolute top-1/2 z-[5] pointer-events-none"
      style={{
        left: getMarkerLeft(percent, horizontalAlign),
        transform: getMarkerTransform(horizontalAlign)
      }}
    >
      <MarkerTooltip placement={placement} horizontalAlign={horizontalAlign}>
        {label}: {formatPriceDollar(amount)}
      </MarkerTooltip>
      <div className="h-2.5 w-2.5 rounded-full border-2 border-white bg-[#FB6011]" />
    </div>
  );
}

export default function EstimateFairRange({
  low,
  high,
  value,
  setValue,
  lastAdsOfferAmount,
  lastCreatorOfferAmount,
  disabled
}: {
  low: number;
  high: number;
  value?: number | string;
  setValue: (value: number | string) => void;
  lastAdsOfferAmount?: number;
  lastCreatorOfferAmount?: number;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const layout = useMemo(() => getFairRangeLayout(low, high), [low, high]);
  const { scaleMin, totalRange, leftOffsetPercent, gradientWidthPercent } =
    layout;

  const brandPercent = useMemo(
    () => getClampedPercent(lastAdsOfferAmount, scaleMin, totalRange),
    [lastAdsOfferAmount, scaleMin, totalRange]
  );
  const creatorPercent = useMemo(
    () => getClampedPercent(lastCreatorOfferAmount, scaleMin, totalRange),
    [lastCreatorOfferAmount, scaleMin, totalRange]
  );
  const hasOfferMarkers = brandPercent != null || creatorPercent != null;
  const markersOverlap = useMemo(() => {
    if (brandPercent == null || creatorPercent == null) return false;
    return Math.abs(brandPercent - creatorPercent) < MARKER_OVERLAP_THRESHOLD;
  }, [brandPercent, creatorPercent]);

  const trackRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  const updateValueFromClientX = useCallback(
    (clientX: number) => {
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect) return;

      const percent = Math.max(
        0,
        Math.min(100, ((clientX - rect.left) / rect.width) * 100)
      );
      setValue(percentToValue(percent, scaleMin, totalRange));
    },
    [setValue, scaleMin, totalRange]
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      isDraggingRef.current = true;
      e.currentTarget.setPointerCapture(e.pointerId);
      updateValueFromClientX(e.clientX);
    },
    [updateValueFromClientX]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDraggingRef.current) return;
      updateValueFromClientX(e.clientX);
    },
    [updateValueFromClientX]
  );

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    isDraggingRef.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
  }, []);

  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      updateValueFromClientX(e.clientX);
    },
    [updateValueFromClientX]
  );

  const numericValue = useMemo(() => {
    if (value === '' || value === undefined || value === null) {
      return null;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }, [value]);

  const sliderPercent =
    numericValue == null
      ? 0
      : Math.max(
          0,
          Math.min(100, valueToPercent(numericValue, scaleMin, totalRange))
        );

  return (
    <div
      className={`w-full overflow-visible ${hasOfferMarkers ? 'pt-[52px]' : 'pt-[10px]'} ${markersOverlap ? 'pb-[52px]' : 'pb-[24px]'}`}
    >
      <div
        ref={trackRef}
        className={`w-full bg-[#E7E5E4] h-[10px] rounded-full relative ${disabled ? 'cursor-default' : 'cursor-pointer'}`}
        onClick={disabled ? undefined : handleTrackClick}
      >
        {brandPercent != null && !!lastAdsOfferAmount && !disabled ? (
          <OfferMarker
            label={t('price.fields.set_your_rate.fair_range_marker_brand', {
              defaultValue: 'Brand'
            })}
            amount={lastAdsOfferAmount}
            percent={brandPercent}
          />
        ) : null}
        {creatorPercent != null && !!lastCreatorOfferAmount && !disabled ? (
          <OfferMarker
            label={t('price.fields.set_your_rate.fair_range_marker_you', {
              defaultValue: 'You'
            })}
            amount={lastCreatorOfferAmount}
            percent={creatorPercent}
            placement={markersOverlap ? 'below' : 'above'}
          />
        ) : null}

        <div
          className="absolute top-0 h-full rounded-[4px] bg-[linear-gradient(90deg,#FFC6A8_0%,#FB6011_45.67%,#BA4D15_100%)]"
          style={{
            left: `${leftOffsetPercent}%`,
            width: `${gradientWidthPercent}%`
          }}
        >
          <div className="text-center justify-start text-[#79716B] text-base font-normal font-['DM_Sans'] leading-6 absolute left-0 top-[15px] translate-x-[-50%]">
            {formatPriceDollar(low)}
          </div>
          <div className="text-center justify-start text-[#79716B] text-base font-normal font-['DM_Sans'] leading-6 absolute top-[15px] right-0 translate-x-[50%]">
            {formatPriceDollar(high)}
          </div>
        </div>
        {disabled ? (
          <div
            className="absolute top-1/2 z-[5] h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-[#FB6011] pointer-events-none"
            style={{ left: `${sliderPercent}%` }}
          />
        ) : (
          <div
            className="absolute z-10 w-[48px] h-[24px] cursor-grab touch-none active:cursor-grabbing"
            style={{
              left: `clamp(0px, calc(${sliderPercent}% - ${THUMB_WIDTH / 2}px), calc(100% - ${THUMB_WIDTH}px))`,
              top: `calc(50% - ${THUMB_HEIGHT / 2}px)`
            }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          >
            <span
              className="pointer-events-none absolute"
              style={{
                left: THUMB_WIDTH / 2 - THUMB_ICON_CENTER_X,
                top: THUMB_HEIGHT / 2 - THUMB_ICON_CENTER_Y
              }}
            >
              <TrashIcon />
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
